import React, {
    CSSProperties,
    ForwardedRef,
    RefObject,
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    AccessibilityProps,
    Platform,
    ReturnKeyTypeOptions,
    Text,
    TextInput,
    TextInputProps,
    TouchableOpacity,
    View,
    ViewStyle,
} from 'react-native';

import { useTranslation } from 'react-i18next';

import IconEye from 'assets/images/Login/ico_eye.svg';
import IconEyeClosed from 'assets/images/Login/ico_eye_closed.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { isWeb } from 'utils/responsiveness';

import LocalizedText from 'components/LocalizedText';

import styles from './styles';
import useTriggerLabelAnimationOnAutofill from './useTriggerLabelAnimationOnAutofill';

type Props = {
    testID?: string;
    errotTextTestID?: string;
    text: string;
    placeholder: string;
    errorText?: string;
    errorColor?: string;
    errorStyle?: ViewStyle;
    onEndEditing(): void;
    onChangeText(text: string): void;
    secureTextEntry?: boolean;
    returnKeyType?: ReturnKeyTypeOptions;
    keyboardType?: TextInputProps['keyboardType'];
    autoCapitalize?: TextInputProps['autoCapitalize'];
    autoCorrect?: TextInputProps['autoCorrect'];
    autoComplete?: TextInputProps['autoComplete'];
    id?: string;
    maxLength?: TextInputProps['maxLength'];
    style?: ViewStyle;
    accessible?: boolean;
    accessibilityLabel?: string;
};

const {
    ACCESSIBILITY: {
        LOGIN: { ACCESSIBILITY_LOGIN_SHOW_HIDE_PASSWORD },
    },
} = LANGUAGE_KEYS;

const styleGoTop = (input: string, focused: boolean, isAutofill: boolean) => {
    if (isAutofill) {
        return styles.inPutPlaceholderTopFocused;
    }

    return input === '' && !focused
        ? styles.inPutPlaceholderTop
        : styles.inPutPlaceholderTopFocused;
};

const CredentialInput = forwardRef(
    (
        {
            testID,
            errotTextTestID,
            text,
            placeholder,
            keyboardType,
            autoCapitalize,
            autoComplete,
            autoCorrect,
            errorText = '',
            errorColor = 'red',
            errorStyle,
            onEndEditing,
            onChangeText,
            secureTextEntry = false,
            returnKeyType = 'done',
            id = '',
            maxLength,
            style,
            accessible,
            accessibilityLabel,
        }: Props,
        ref: ForwardedRef<typeof TouchableOpacity>,
    ) => {
        const { t } = useTranslation();

        const [textFieldFocused, setTextFieldFocused] = useState(false);
        const [forceShowPassword, setForceShowPassword] = useState(false);
        const textRef = useRef<TextInput>(undefined);
        const errorTextRef = useRef<Text>(undefined);

        const { isAutofill } = useTriggerLabelAnimationOnAutofill({
            id,
            textFieldFocused,
        });

        const webFocusableProps = useMemo(
            () => ({
                'aria-label': t(errorText),
                'aria-live': 'assertive' as AccessibilityProps['aria-live'],
                tabIndex: -1,
            }),
            [t, errorText],
        );

        const handlePress = useCallback(() => {
            if (textRef.current) {
                textRef.current.focus();
            }
        }, []);

        const handleFocus = useCallback(() => {
            setTextFieldFocused(true);
        }, []);

        const handleBlur = useCallback(() => {
            setTextFieldFocused(false);
        }, []);

        const handleEndEditing = useCallback(() => {
            setTextFieldFocused(false);
            setForceShowPassword(false);
            onEndEditing();
        }, [onEndEditing]);

        const handleEyePress = useCallback(
            () => setForceShowPassword(!forceShowPassword),
            [forceShowPassword],
        );

        const renderInputLabel = useCallback(() => {
            if (isWeb) {
                <label
                    style={
                        styleGoTop(
                            text,
                            textFieldFocused,
                            isAutofill,
                        ) as unknown as CSSProperties
                    }
                    htmlFor={id}
                >
                    {t(placeholder)}
                </label>;
            }

            return (
                <LocalizedText
                    textKey={placeholder}
                    style={styleGoTop(text, textFieldFocused, isAutofill)}
                />
            );
        }, [t, id, isAutofill, textFieldFocused, placeholder, text]);

        useEffect(() => {
            if (errorText) {
                setAccessibilityFocusTo(errorTextRef);
            }
        }, [errorText]);

        return (
            <>
                <View style={styles.root}>
                    <TouchableOpacity
                        // @ts-ignore
                        ref={ref}
                        style={[styles.inPutView, style]}
                        onPress={handlePress}
                        activeOpacity={1}
                        // @ts-ignore
                        tabIndex={-1}
                        accessible={!isWeb && accessible}
                        accessibilityLabel={
                            !isWeb ? accessibilityLabel : undefined
                        }
                    >
                        {renderInputLabel()}
                        <TextInput
                            id={id}
                            testID={testID}
                            importantForAccessibility="no-hide-descendants"
                            accessibilityLabel={
                                isWeb ? accessibilityLabel : undefined
                            }
                            {...(isWeb && { tabIndex: 0 })}
                            ref={textRef as never}
                            value={text}
                            keyboardType={keyboardType}
                            style={styles.inPutPlaceholder}
                            secureTextEntry={
                                secureTextEntry && !forceShowPassword
                            }
                            autoCapitalize={autoCapitalize}
                            autoCorrect={autoCorrect}
                            autoComplete={autoComplete}
                            onChangeText={(txt) => onChangeText(txt)}
                            onFocus={handleFocus}
                            onEndEditing={handleEndEditing}
                            onBlur={handleBlur}
                            returnKeyType={returnKeyType}
                            maxLength={maxLength}
                            disableFullscreenUI
                        />
                    </TouchableOpacity>
                    {secureTextEntry && (
                        <TouchableOpacity
                            accessible
                            accessibilityLabel={t(
                                ACCESSIBILITY_LOGIN_SHOW_HIDE_PASSWORD,
                            )}
                            accessibilityRole={Platform.select({
                                web: 'imagebutton',
                                default: 'button',
                            })}
                            role="button"
                            style={styles.eye}
                            onPress={handleEyePress}
                        >
                            {forceShowPassword ? (
                                <IconEye />
                            ) : (
                                <IconEyeClosed />
                            )}
                        </TouchableOpacity>
                    )}
                </View>
                {errorText !== '' && (
                    <LocalizedText
                        ref={errorTextRef as RefObject<Text>}
                        testID={errotTextTestID}
                        style={[
                            styles.inputError,
                            errorStyle,
                            { color: errorColor },
                        ]}
                        textKey={errorText}
                        {...(isWeb && webFocusableProps)}
                        accessible
                        accessibilityRole="text"
                        {...(isWeb && { tabIndex: 0 })}
                        accessibilityLabel={t(errorText)}
                    />
                )}
            </>
        );
    },
);

export default CredentialInput;
